// WebGL2 canvas renderer.
//
// Architecture:
//  * A display list (GPU buffers + batches) is rebuilt only when the document changes.
//    Camera movement just changes a uniform, so pan/zoom cost is independent of document size.
//  * Rect / rounded-rect / ellipse fills, strokes (inside/center/outside), 2-stop gradients,
//    image fills and drop shadows are instanced quads shaded with analytic SDFs.
//  * Vector paths are tessellated on the CPU (cached per node) into one triangle stream (MSAA).
//  * Text and effect-heavy nodes are rasterized with the portable Canvas2D renderer into
//    atlas pages at a zoom bucket, progressively and under a per-frame time budget.

import {
  hasArc,
  dashOutline,
  lineSegment,
  Renderer2D,
  hasDrawStroke,
  fillTextRun,
  layoutNodeText,
  textPadding,
  textBox,
  textStyle,
  nodeOutline,
  flattenPath,
  connectorGeometry,
  visualBounds,
  cornerRadii,
  mul,
  apply,
  IDENTITY,
  type Document,
  type Mat,
  type NodeData,
  type NodeId,
  type Paint,
  type Rect,
  type Vec2,
  type Color,
  type Effect,
} from '@vellum/core';
import type { App } from '../app.ts';
import { fillTriangles, strokeTriangles, dashPolyline, type Cap, type Join } from './tessellate.ts';

const INST_FLOATS = 44;
const MESH_FLOATS = 10; // x y r g b a clip(4)
const NO_CLIP: [number, number, number, number] = [-1e9, -1e9, 1e9, 1e9];
const ATLAS = 2048;
const LAYERS = 4;
/** Text smaller than this many device pixels is drawn as greeked bars instead of rasters. */
const LOD_PX = 5;

const KIND_RECT = 0,
  KIND_ELLIPSE = 1,
  KIND_ATLAS = 2,
  KIND_IMAGE = 3,
  KIND_SHADOW_RECT = 4,
  KIND_SHADOW_ELLIPSE = 5;
const FILL_NONE = 0,
  FILL_SOLID = 1,
  FILL_LINEAR = 2,
  FILL_RADIAL = 3;

const INST_VS = `#version 300 es
layout(location=0) in vec2 aCorner;
layout(location=1) in vec4 iM0;
layout(location=2) in vec4 iM1;
layout(location=3) in vec4 iRadii;
layout(location=4) in vec4 iParams;
layout(location=5) in vec4 iFill;
layout(location=6) in vec4 iFill2;
layout(location=7) in vec4 iGrad;
layout(location=8) in vec4 iStroke;
layout(location=9) in vec4 iClip;
layout(location=10) in vec4 iUV;
layout(location=11) in vec4 iExtra;
uniform mat3 uView;
uniform float uPx;
uniform float uLod;
out vec2 vLocal;
out vec2 vWorld;
flat out vec2 vSize;
flat out vec4 vRadii;
flat out vec4 vParams;
flat out vec4 vFill;
flat out vec4 vFill2;
flat out vec4 vGrad;
flat out vec4 vStroke;
flat out vec4 vClip;
flat out vec4 vUV;
flat out vec4 vExtra;
out float vAA;
void main() {
  vec2 size = iM1.zw;
  mat2 lin = mat2(iM0.xy, iM0.zw);
  float scale = sqrt(abs(determinant(lin))) * uPx;
  float aa = 1.0 / max(scale, 1e-6);
  float kind = iParams.x;
  // Level of detail: w > 0 = greeked text (shown when small), w < 0 = text raster (shown when large).
  if (iExtra.w != 0.0) {
    float px = abs(iExtra.w) * scale;
    bool show = iExtra.w > 0.0 ? px < uLod : px >= uLod;
    if (!show) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
  }
  float expand = aa * 1.5;
  if (iParams.y > 0.0 && iParams.z > 0.5 && kind < 1.5) expand += iParams.y * (iParams.z > 1.5 ? 1.0 : 0.5);
  if (kind > 3.5) expand += iExtra.y * 3.0 + abs(iExtra.z);
  if (kind > 1.5 && kind < 3.5) expand = 0.0;
  vec2 local = aCorner * (size + 2.0 * expand) - expand;
  vec2 world = lin * local + iM1.xy;
  vec3 c = uView * vec3(world, 1.0);
  gl_Position = vec4(c.xy, 0.0, 1.0);
  vLocal = local; vWorld = world; vSize = size; vRadii = iRadii; vParams = iParams;
  vFill = iFill; vFill2 = iFill2; vGrad = iGrad; vStroke = iStroke; vClip = iClip; vUV = iUV; vExtra = iExtra; vAA = aa;
}`;

const INST_FS = `#version 300 es
precision highp float;
in vec2 vLocal;
in vec2 vWorld;
flat in vec2 vSize;
flat in vec4 vRadii;
flat in vec4 vParams;
flat in vec4 vFill;
flat in vec4 vFill2;
flat in vec4 vGrad;
flat in vec4 vStroke;
flat in vec4 vClip;
flat in vec4 vUV;
flat in vec4 vExtra;
in float vAA;
uniform highp sampler2DArray uAtlas;
uniform sampler2D uImage;
out vec4 outColor;

float sdRoundBox(vec2 p, vec2 b, vec4 r) {
  float rad = p.x > 0.0 ? (p.y > 0.0 ? r.z : r.y) : (p.y > 0.0 ? r.w : r.x);
  vec2 q = abs(p) - b + rad;
  return min(max(q.x, q.y), 0.0) + length(max(q, 0.0)) - rad;
}
float sdEllipse(vec2 p, vec2 ab) {
  ab = max(ab, vec2(1e-4));
  float k0 = length(p / ab);
  float k1 = length(p / (ab * ab));
  return k1 < 1e-6 ? -min(ab.x, ab.y) : k0 * (k0 - 1.0) / k1;
}
float cov(float d) { return clamp(0.5 - d / vAA, 0.0, 1.0); }
float erf(float x) {
  float s = sign(x), a = abs(x);
  x = 1.0 + (0.278393 + (0.230389 + 0.078108 * (a * a)) * a) * a;
  x *= x;
  return s - s / (x * x);
}
vec4 premul(vec4 c) { return vec4(c.rgb * c.a, c.a); }

void main() {
  if (vWorld.x < vClip.x || vWorld.y < vClip.y || vWorld.x > vClip.z || vWorld.y > vClip.w) discard;
  float kind = vParams.x;
  float opacity = vParams.w;
  vec2 half_ = vSize * 0.5;
  vec2 p = vLocal - half_;
  if (kind > 3.5) {
    // analytic gaussian shadow of a (spread) rounded box / ellipse
    float spread = vExtra.z;
    float sigma = max(vExtra.y, 0.3);
    float d = kind < 4.5 ? sdRoundBox(p, half_ + spread, vRadii + spread) : sdEllipse(p, half_ + spread);
    float a = 0.5 - 0.5 * erf(d / (sigma * 1.41421356));
    outColor = premul(vFill) * a * opacity;
    return;
  }
  if (kind > 1.5) {
    vec2 uv = mix(vUV.xy, vUV.zw, clamp(vLocal / vSize, 0.0, 1.0));
    vec4 t = kind < 2.5 ? texture(uAtlas, vec3(uv, vParams.y)) : texture(uImage, uv);
    float m = 1.0;
    if (vRadii != vec4(0.0)) m = cov(sdRoundBox(p, half_, vRadii));
    outColor = t * m * opacity;
    return;
  }
  float d = kind < 0.5 ? sdRoundBox(p, half_, vRadii) : sdEllipse(p, half_);
  float fc = cov(d);
  vec4 fill = vec4(0.0);
  float ft = vExtra.x;
  if (ft > 0.5) {
    vec4 c = vFill;
    if (ft > 1.5) {
      vec2 uv = vLocal / max(vSize, vec2(1e-4));
      vec2 a = vGrad.xy, b = vGrad.zw;
      float t;
      if (ft < 2.5) { vec2 ab = b - a; t = dot(uv - a, ab) / max(dot(ab, ab), 1e-6); }
      else t = length((uv - a) * vSize) / max(length((b - a) * vSize), 1e-6);
      c = mix(vFill, vFill2, clamp(t, 0.0, 1.0));
    }
    fill = premul(c) * fc;
  }
  float w = vParams.y;
  float align = vParams.z;
  float sc = 0.0;
  if (w > 0.0 && vStroke.a > 0.0) {
    if (align < 0.5) sc = fc - cov(d + w);
    else if (align < 1.5) sc = cov(d - w * 0.5) - cov(d + w * 0.5);
    else sc = cov(d - w) - fc;
    sc = clamp(sc, 0.0, 1.0);
  }
  vec4 stroke = premul(vStroke) * sc;
  vec4 col = stroke + fill * (1.0 - stroke.a);
  if (col.a <= 0.0) discard;
  outColor = col * opacity;
}`;

const MESH_VS = `#version 300 es
layout(location=0) in vec2 aPos;
layout(location=1) in vec4 aColor;
layout(location=2) in vec4 aClip;
uniform mat3 uView;
out vec4 vColor;
out vec2 vWorld;
flat out vec4 vClip;
void main() {
  vec3 c = uView * vec3(aPos, 1.0);
  gl_Position = vec4(c.xy, 0.0, 1.0);
  vColor = aColor; vWorld = aPos; vClip = aClip;
}`;

const MESH_FS = `#version 300 es
precision highp float;
in vec4 vColor;
in vec2 vWorld;
flat in vec4 vClip;
out vec4 outColor;
void main() {
  if (vWorld.x < vClip.x || vWorld.y < vClip.y || vWorld.x > vClip.z || vWorld.y > vClip.w) discard;
  outColor = vColor;
}`;

interface Batch {
  type: 'inst' | 'mesh';
  start: number;
  count: number;
  tex: WebGLTexture | null;
  /** blend mode index (BLEND_MODES); 0 = normal compositing */
  blend?: number;
  /** background blur / glass: composited against what's drawn below (same object = same layer) */
  backdrop?: BackdropFx;
}

/** A layer's backdrop effect, in page units. */
interface BackdropFx {
  kind: 0 | 1; // 0 = background blur, 1 = glass
  radius: number;
  /** glass: magnification of the backdrop, and the channel split */
  scale: number;
  dispersion: number;
  /** page-space center of the layer (glass magnifies about it) */
  cx: number;
  cy: number;
}

/** Separable Gaussian blur (one direction per pass), sampling at a stride for large radii. */
const BLUR_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uSrc;
uniform vec2 uDir; // texel step in uv
uniform float uSigma; // in texels
out vec4 outColor;
void main() {
  float stride = max(1.0, uSigma / 4.0);
  vec4 acc = vec4(0.0);
  float wsum = 0.0;
  for (int i = -12; i <= 12; i++) {
    float x = float(i) * stride;
    float w = exp(-(x * x) / (2.0 * uSigma * uSigma + 1e-4));
    acc += texture(uSrc, vUv + uDir * x) * w;
    wsum += w;
  }
  outColor = acc / wsum;
}`;

/** Composite a layer over its backdrop, with the backdrop blurred / refracted inside the layer's shape. */
const BACKDROP_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uLayer;
uniform sampler2D uBackdrop;
uniform sampler2D uBlurred;
uniform int uKind;
uniform float uHasBlur;
uniform vec2 uCenter; // uv
uniform float uScale;
uniform float uDisp;
out vec4 outColor;
vec3 pick(vec2 uv) { return uHasBlur > 0.5 ? texture(uBlurred, uv).rgb : texture(uBackdrop, uv).rgb; }
void main() {
  vec4 l = texture(uLayer, vUv);
  vec3 b = texture(uBackdrop, vUv).rgb;
  // the layer's own pixels mark its shape (rasters add a 2/255 fill for exactly this)
  float inside = smoothstep(0.0, 2.0 / 255.0, l.a);
  vec3 fx;
  if (uKind == 1) {
    vec2 d = vUv - uCenter;
    fx = vec3(pick(uCenter + d / (uScale * (1.0 + uDisp))).r, pick(uCenter + d / uScale).g, pick(uCenter + d / (uScale * (1.0 - uDisp))).b);
  } else fx = pick(vUv);
  vec3 base = mix(b, fx, inside);
  outColor = vec4(l.rgb + (1.0 - l.a) * base, 1.0);
}`;

/** Order matches the composite shader's mode numbers. */
const BLEND_MODES = ['NORMAL', 'MULTIPLY', 'SCREEN', 'OVERLAY', 'DARKEN', 'LIGHTEN', 'COLOR_DODGE', 'COLOR_BURN', 'HARD_LIGHT', 'SOFT_LIGHT', 'DIFFERENCE', 'EXCLUSION', 'LINEAR_BURN', 'LINEAR_DODGE', 'HUE', 'SATURATION', 'COLOR', 'LUMINOSITY'];
const blendIndex = (m: string | undefined) => (m && m !== 'PASS_THROUGH' ? Math.max(0, BLEND_MODES.indexOf(m)) : 0);

// Blending a layer onto the backdrop (W3C compositing formulas; the canvas backdrop is opaque)
const BLEND_VS = `#version 300 es
out vec2 vUv;
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  vUv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

const BLEND_FS = `#version 300 es
precision highp float;
in vec2 vUv;
uniform sampler2D uLayer;
uniform sampler2D uBackdrop;
uniform int uMode;
out vec4 outColor;
float lum(vec3 c) { return dot(c, vec3(0.3, 0.59, 0.11)); }
vec3 clipColor(vec3 c) {
  float l = lum(c), n = min(min(c.r, c.g), c.b), x = max(max(c.r, c.g), c.b);
  if (n < 0.0) c = l + (c - l) * l / (l - n);
  if (x > 1.0) c = l + (c - l) * (1.0 - l) / (x - l);
  return c;
}
vec3 setLum(vec3 c, float l) { return clipColor(c + (l - lum(c))); }
float sat(vec3 c) { return max(max(c.r, c.g), c.b) - min(min(c.r, c.g), c.b); }
vec3 setSat(vec3 c, float s) {
  float mx = max(max(c.r, c.g), c.b), mn = min(min(c.r, c.g), c.b);
  return mx > mn ? (c - mn) * s / (mx - mn) : vec3(0.0);
}
float dodge(float b, float s) { return b == 0.0 ? 0.0 : s >= 1.0 ? 1.0 : min(1.0, b / (1.0 - s)); }
float burn(float b, float s) { return b >= 1.0 ? 1.0 : s <= 0.0 ? 0.0 : 1.0 - min(1.0, (1.0 - b) / s); }
float hard(float b, float s) { return s <= 0.5 ? b * 2.0 * s : 1.0 - (1.0 - b) * (1.0 - (2.0 * s - 1.0)); }
float soft(float b, float s) {
  float d = b <= 0.25 ? ((16.0 * b - 12.0) * b + 4.0) * b : sqrt(b);
  return s <= 0.5 ? b - (1.0 - 2.0 * s) * b * (1.0 - b) : b + (2.0 * s - 1.0) * (d - b);
}
vec3 blend(vec3 b, vec3 s) {
  if (uMode == 1) return b * s;
  if (uMode == 2) return b + s - b * s;
  if (uMode == 3) return vec3(hard(s.r, b.r), hard(s.g, b.g), hard(s.b, b.b));
  if (uMode == 4) return min(b, s);
  if (uMode == 5) return max(b, s);
  if (uMode == 6) return vec3(dodge(b.r, s.r), dodge(b.g, s.g), dodge(b.b, s.b));
  if (uMode == 7) return vec3(burn(b.r, s.r), burn(b.g, s.g), burn(b.b, s.b));
  if (uMode == 8) return vec3(hard(b.r, s.r), hard(b.g, s.g), hard(b.b, s.b));
  if (uMode == 9) return vec3(soft(b.r, s.r), soft(b.g, s.g), soft(b.b, s.b));
  if (uMode == 10) return abs(b - s);
  if (uMode == 11) return b + s - 2.0 * b * s;
  if (uMode == 12) return max(b + s - 1.0, 0.0);
  if (uMode == 13) return min(b + s, 1.0);
  if (uMode == 14) return setLum(setSat(s, sat(b)), lum(b));
  if (uMode == 15) return setLum(setSat(b, sat(s)), lum(b));
  if (uMode == 16) return setLum(s, lum(b));
  if (uMode == 17) return setLum(b, lum(s));
  return s;
}
void main() {
  vec4 l = texture(uLayer, vUv);
  vec3 b = texture(uBackdrop, vUv).rgb;
  if (l.a <= 0.0) { outColor = vec4(b, 1.0); return; }
  vec3 s = l.rgb / l.a;
  outColor = vec4(mix(b, blend(b, s), l.a), 1.0);
}`;

interface Raster {
  layer: AtlasLayer;
  x: number;
  y: number;
  w: number;
  h: number;
  scale: number;
  version: string;
  /** placement of the raster relative to the node (or the page for 'subtree') */
  ox: number;
  oy: number;
  lw: number;
  lh: number;
  /** layer generation this raster was written in */
  gen: number;
  /** Partial raster: only this region (raster-local coords) was rendered, for huge zoom levels. */
  partial?: boolean;
  /** Oversize rasters live in their own texture instead of the atlas. */
  tex?: WebGLTexture;
}

interface RasterRequest {
  key: string;
  id: NodeId;
  kind: 'text' | 'nodetext' | 'subtree';
  bounds: Rect; // page AABB (for visibility)
  fontSize: number;
}

/** Instance slots of a raster-backed item, so finished rasters can be patched in place. */
interface RasterSlot {
  inst: number;
  greek: number[];
  alpha: number;
  clip: number[];
}

/** One layer of the shared atlas texture array, packed with shelves. */
class AtlasLayer {
  shelves: { y: number; h: number; x: number }[] = [];
  nextY = 0;
  lastUsed = 0;
  gen = 0;
  constructor(readonly index: number) {}
  alloc(w: number, h: number): { x: number; y: number } | null {
    w += 2;
    h += 2;
    for (const s of this.shelves) {
      if (h <= s.h && h >= s.h * 0.6 && s.x + w <= ATLAS) {
        const r = { x: s.x + 1, y: s.y + 1 };
        s.x += w;
        return r;
      }
    }
    if (this.nextY + h > ATLAS || w > ATLAS) return null;
    const s = { y: this.nextY, h, x: w };
    this.shelves.push(s);
    this.nextY += h;
    return { x: 1, y: s.y + 1 };
  }
  reset() {
    this.shelves = [];
    this.nextY = 0;
    this.gen++;
  }
}

class Growable {
  data: Float32Array;
  length = 0;
  constructor(initial: number) {
    this.data = new Float32Array(initial);
  }
  reserve(n: number) {
    if (this.length + n <= this.data.length) return;
    let cap = this.data.length * 2;
    while (cap < this.length + n) cap *= 2;
    const d = new Float32Array(cap);
    d.set(this.data.subarray(0, this.length));
    this.data = d;
  }
}

function paintColor(p: Extract<Paint, { type: 'SOLID' }>, alpha = 1): [number, number, number, number] {
  return [p.color.r, p.color.g, p.color.b, p.color.a * (p.opacity ?? 1) * alpha];
}

function c4(c: Color, a = 1): [number, number, number, number] {
  return [c.r, c.g, c.b, c.a * a];
}

export interface RenderStats {
  instances: number;
  meshTriangles: number;
  batches: number;
  buildMs: number;
  /** last incremental (in-place) update */
  patchMs: number;
  rasterMs: number;
  pendingRasters: number;
}

export class GLRenderer {
  readonly gl: WebGL2RenderingContext;
  private instProg: WebGLProgram;
  private meshProg: WebGLProgram;
  private instVao: WebGLVertexArrayObject;
  private meshVao: WebGLVertexArrayObject;
  private instBuf: WebGLBuffer;
  private meshBuf: WebGLBuffer;
  private quadBuf: WebGLBuffer;
  private uViewInst: WebGLUniformLocation;
  private uPxInst: WebGLUniformLocation;
  private uLod: WebGLUniformLocation;
  private uAtlas: WebGLUniformLocation;
  private uImage: WebGLUniformLocation;
  private atlasTex: WebGLTexture;
  private layers: AtlasLayer[] = [];
  private slots = new Map<string, RasterSlot>();
  private uViewMesh: WebGLUniformLocation;
  private inst = new Growable(INST_FLOATS * 1024);
  private mesh = new Growable(MESH_FLOATS * 4096);
  private batches: Batch[] = [];
  /** blend mode of the node being encoded, and whether the next primitive must start a batch */
  private blendNow = 0;
  private backdropNow: BackdropFx | undefined;
  private blurTargets: { w: number; h: number; a: WebGLTexture; b: WebGLTexture; fa: WebGLFramebuffer; fb: WebGLFramebuffer } | null = null;
  private splitBatch = false;
  private blendProg: WebGLProgram;
  private blurProg!: WebGLProgram;
  private backdropProg!: WebGLProgram;
  private blendTargets: { w: number; h: number; layer: WebGLTexture; fbo: WebGLFramebuffer; backdrop: WebGLTexture } | null = null;
  private dirty = true;
  private builtVersion = -1;
  private builtPage = '';
  private builtHidden = '';
  private builtImages = 0;
  private builtBucket = 0;
  private bucketTimer: ReturnType<typeof setTimeout> | null = null;
  private rasters = new Map<string, Raster>();
  private requests = new Map<string, RasterRequest>();
  /** Per-node instance/mesh ranges from the last build, for in-place incremental updates. */
  private ranges = new Map<NodeId, { i0: number; i1: number; m0: number; m1: number; alpha: number; clip: [number, number, number, number] }>();
  private changedIds = new Set<NodeId>();
  private patching = false;
  private patchFailed = false;
  private sigCache = new Map<string, { ver: string; sig: string }>();
  /** Whether any layer uses a pattern fill or custom brush (checked once per document version). */
  private depsAt = -1;
  private hasDeps = false;
  private versions = new Map<NodeId, number>();
  private geomCache = new Map<string, { fill: number[]; stroke: number[]; base: string; tol: number; flat: boolean }>();
  /** geomSig per layer version (it includes the whole path, so building it is not free) */
  private geomSigs = new Map<NodeId, { ver: number; sig: string }>();
  /** the zoom range the meshes were built for, and whether any of them has curves (see tolerance) */
  private builtBand = 0;
  private curved = false;
  /** world bounds of layers drawn off screen from coarser cached meshes, refined once they come into view */
  private coarse = new Map<NodeId, Rect>();
  /** the view (with a margin) meshes are refined for during a build */
  private refineRect: Rect | null = null;
  private imageTex = new Map<string, WebGLTexture>();
  /**
   * Uploaded images, drawn from copies sized to how big they appear (ImageCache.want): one texture
   * per image whose contents get sharper (or coarser) without touching the instances that use it.
   */
  private levelTex = new Map<string, { tex: WebGLTexture; level: number; pixels: number; lastSeen: number }>();
  /** Where each layer draws an uploaded image: world box, and how many image pixels it shows per unit of zoom. */
  private imageUses = new Map<NodeId, { src: string; x0: number; y0: number; x1: number; y1: number; dim: number; sized: boolean }[]>();
  private uploads = new Set<string>();
  private wantsTimer: ReturnType<typeof setTimeout> | null = null;
  private wantsCamera = '';
  private bigTex = new Map<string, WebGLTexture>();
  private maxTex = 4096;
  private r2d: Renderer2D | null = null;
  private r2dDoc: Document | null = null;
  private rasterCanvas: OffscreenCanvas | HTMLCanvasElement;
  private frame = 0;
  private offDoc: (() => void) | null = null;
  private watchedDoc: Document | null = null;
  width = 1;
  height = 1;
  dpr = 1;
  stats: RenderStats = { instances: 0, meshTriangles: 0, batches: 0, buildMs: 0, patchMs: 0, rasterMs: 0, pendingRasters: 0 };
  /** Nodes hidden from rendering (e.g. the text being edited). */
  hidden = new Set<NodeId>();

  constructor(
    readonly canvas: HTMLCanvasElement,
    private app: App,
  ) {
    const gl = canvas.getContext('webgl2', { antialias: true, alpha: false, premultipliedAlpha: true, preserveDrawingBuffer: false, powerPreference: 'high-performance' });
    if (!gl) throw new Error('WebGL2 not available');
    this.gl = gl;
    app.images.onLevel = (src) => {
      this.uploads.add(src);
      app.requestRender();
    };
    this.instProg = program(gl, INST_VS, INST_FS);
    this.meshProg = program(gl, MESH_VS, MESH_FS);
    this.blendProg = program(gl, BLEND_VS, BLEND_FS);
    this.blurProg = program(gl, BLEND_VS, BLUR_FS);
    this.backdropProg = program(gl, BLEND_VS, BACKDROP_FS);
    this.uViewInst = gl.getUniformLocation(this.instProg, 'uView')!;
    this.uPxInst = gl.getUniformLocation(this.instProg, 'uPx')!;
    this.uLod = gl.getUniformLocation(this.instProg, 'uLod')!;
    this.uAtlas = gl.getUniformLocation(this.instProg, 'uAtlas')!;
    this.uImage = gl.getUniformLocation(this.instProg, 'uImage')!;
    this.atlasTex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlasTex);
    gl.texStorage3D(gl.TEXTURE_2D_ARRAY, 1, gl.RGBA8, ATLAS, ATLAS, LAYERS);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D_ARRAY, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    for (let i = 0; i < LAYERS; i++) this.layers.push(new AtlasLayer(i));
    this.uViewMesh = gl.getUniformLocation(this.meshProg, 'uView')!;

    this.quadBuf = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW);

    this.instBuf = gl.createBuffer()!;
    this.instVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.instVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuf);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
    for (let i = 0; i < 11; i++) {
      gl.enableVertexAttribArray(1 + i);
      gl.vertexAttribPointer(1 + i, 4, gl.FLOAT, false, INST_FLOATS * 4, i * 16);
      gl.vertexAttribDivisor(1 + i, 1);
    }

    this.meshBuf = gl.createBuffer()!;
    this.meshVao = gl.createVertexArray()!;
    gl.bindVertexArray(this.meshVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.meshBuf);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, MESH_FLOATS * 4, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 4, gl.FLOAT, false, MESH_FLOATS * 4, 8);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 4, gl.FLOAT, false, MESH_FLOATS * 4, 24);
    gl.bindVertexArray(null);

    this.rasterCanvas = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(256, 256) : document.createElement('canvas');
    this.maxTex = Math.min(8192, gl.getParameter(gl.MAX_TEXTURE_SIZE) as number);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  }

  private builtFonts = 0;

  invalidate() {
    this.dirty = true;
  }

  resize(cssW: number, cssH: number, dpr: number) {
    this.dpr = dpr;
    this.width = Math.max(1, Math.round(cssW * dpr));
    this.height = Math.max(1, Math.round(cssH * dpr));
    if (this.canvas.width !== this.width) this.canvas.width = this.width;
    if (this.canvas.height !== this.height) this.canvas.height = this.height;
  }

  private watch(doc: Document) {
    if (this.watchedDoc === doc) return;
    this.offDoc?.();
    this.watchedDoc = doc;
    this.versions.clear();
    this.rasters.clear();
    this.geomCache.clear();
    this.geomSigs.clear();
    this.sigCache.clear();
    this.dirty = true;
    for (const l of this.layers) l.reset();
    this.offDoc = doc.on((ev) => {
      const bump = (id: NodeId) => this.versions.set(id, (this.versions.get(id) ?? 0) + 1);
      for (const id of ev.changed) {
        bump(id);
        for (const a of doc.ancestors(id)) bump(a.id);
      }
      for (const id of ev.deleted) {
        this.versions.delete(id);
        this.geomSigs.delete(id);
        for (const k of ['text', 'nodetext', 'subtree']) this.rasters.delete(`${k}:${id}`);
      }
      if (ev.structural) this.dirty = true;
      else for (const id of ev.changed) this.changedIds.add(id);
    });
  }

  private ver(id: NodeId) {
    return this.versions.get(id) ?? 0;
  }

  /** Versions of the layers a subtree's pattern fills and custom brushes draw from. */
  private sourceVersions(id: NodeId): string {
    const doc = this.app.doc;
    if (this.depsAt !== doc.version) {
      this.depsAt = doc.version;
      this.hasDeps = false;
      for (const n of doc.nodes.values())
        if (n.strokeBrush?.sourceId || n.fills.some((p) => p.type === 'PATTERN' || p.type === 'SHADER') || n.effects.some((e) => e.type === 'SHADER')) {
          this.hasDeps = true;
          break;
        }
    }
    if (!this.hasDeps) return '';
    let out = '';
    doc.walk(id, (c) => {
      // shader results arrive asynchronously: each one bumps the layer's shader version
      if (c.fills.some((p) => p.type === 'SHADER') || c.effects.some((e) => e.type === 'SHADER')) out += `|sh${c.id}:${this.app.shaders.versionOf(c.id)}`;
      const srcs = [...c.fills.filter((p) => p.type === 'PATTERN').map((p) => (p as { sourceId: NodeId }).sourceId), ...(c.strokeBrush?.sourceId ? [c.strokeBrush.sourceId] : [])];
      for (const src of srcs) {
        let v = 0;
        if (doc.has(src)) doc.walk(src, (d) => void (v = Math.max(v, this.ver(d.id))));
        out += `|${src}:${v}`;
      }
    });
    return out;
  }

  /** Draw a frame. Returns true if more work (rasters) is pending. */
  render(): boolean {
    const app = this.app;
    const doc = app.doc;
    this.watch(doc);
    this.frame++;
    if (this.r2dDoc !== doc) {
      this.r2d = new Renderer2D(doc);
      this.r2dDoc = doc;
    }
    const hiddenKey = [...this.hidden].join(',');
    // Paths are tessellated for the zoom level; re-tessellate once zooming settles.
    // Curves are flattened for a zoom range (see tolerance): rebuild once zooming settles past it.
    // Zooming out keeps the finer meshes unless they got far finer than needed.
    const bucket = this.bucket();
    const band = this.geomBand();
    const inView = () => {
      if (!this.coarse.size) return false;
      const v = app.viewportRect();
      for (const b of this.coarse.values()) if (b.x <= v.x + v.width && b.x + b.width >= v.x && b.y <= v.y + v.height && b.y + b.height >= v.y) return true;
      return false;
    };
    const stale = () => (this.curved && (this.geomBand() > this.builtBand || this.geomBand() * 16 < this.builtBand)) || inView();
    if (stale() && !this.bucketTimer) {
      this.bucketTimer = setTimeout(() => {
        this.bucketTimer = null;
        if (stale()) {
          this.dirty = true;
          this.app.requestRender();
        }
      }, 180);
    }
    const uploadsLeft = this.uploadImages(performance.now() + 6);
    // once the view settles, images on screen get as sharp as they're now shown
    const cam = `${app.camera.x | 0},${app.camera.y | 0},${app.camera.zoom}`;
    if (cam !== this.wantsCamera && this.imageUses.size) {
      this.wantsCamera = cam;
      this.scheduleWants();
    }
    const full = this.dirty || this.builtPage !== app.editor.pageId || this.builtHidden !== hiddenKey || this.builtImages !== app.imagesVersion;
    if (!full && this.changedIds.size) {
      const t0 = performance.now();
      if (this.patch()) {
        this.stats.patchMs = performance.now() - t0;
        // a resized image may need a sharper copy
        if (this.imageUses.size) this.scheduleWants();
      } else this.dirty = true;
    }
    if (this.builtFonts !== app.fontsVersion) {
      // a feature font finished loading: every text raster may look different now
      this.builtFonts = app.fontsVersion;
      this.rasters.clear();
      this.sigCache.clear();
      this.dirty = true;
    }
    if (this.builtImages !== app.imagesVersion) {
      // subtree rasters drawn before an image finished loading are missing it
      for (const k of [...this.rasters.keys()]) if (k.startsWith('subtree:')) this.rasters.delete(k);
    }
    if (this.dirty || full) {
      const t0 = performance.now();
      this.build();
      this.stats.buildMs = performance.now() - t0;
      this.builtVersion = app.docVersion;
      this.builtPage = app.editor.pageId;
      this.builtHidden = hiddenKey;
      this.builtImages = app.imagesVersion;
      this.builtBucket = bucket;
      this.builtBand = band;
      this.dirty = false;
      this.changedIds.clear();
    }
    // Progressive rasterization of visible text/effects within a time budget.
    const t1 = performance.now();
    this.processRasters(t1 + 8);
    this.stats.rasterMs = performance.now() - t1;
    this.draw();
    // a raster that needs a rebuild (e.g. moved to its own texture) must get another frame
    return this.stats.pendingRasters > 0 || this.dirty || uploadsLeft || this.changedIds.size > 0;
  }

  // ---------------------------------------------------------------- build

  private bucket(): number {
    // √2 steps: rasters are at most 1.41× oversampled (crisp when downscaled) and never upscaled.
    const z = this.app.camera.zoom * this.dpr;
    return Math.pow(2, Math.max(-6, Math.min(14, Math.ceil(Math.log2(z) * 2 - 0.1))) / 2);
  }

  private build() {
    const app = this.app;
    const doc = app.doc;
    this.inst.length = 0;
    this.mesh.length = 0;
    this.batches = [];
    this.layerStarts.clear();
    this.requests.clear();
    this.slots.clear();
    this.ranges.clear();
    this.imageUses.clear();
    this.curved = false;
    this.coarse.clear();
    const v = app.viewportRect();
    this.refineRect = { x: v.x - v.width * 0.5, y: v.y - v.height * 0.5, width: v.width * 2, height: v.height * 2 };
    const page = doc.get(app.editor.pageId);
    if (!page) return;
    for (const c of doc.children(page.id)) this.visit(doc, c, IDENTITY, 1, NO_CLIP);
    this.wantImages();
    this.stats.instances = this.inst.length / INST_FLOATS;
    this.stats.meshTriangles = this.mesh.length / MESH_FLOATS / 3;
    this.stats.batches = this.batches.length;
    const gl = this.gl;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.inst.data.subarray(0, this.inst.length), gl.DYNAMIC_DRAW);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.meshBuf);
    gl.bufferData(gl.ARRAY_BUFFER, this.mesh.data.subarray(0, this.mesh.length), gl.DYNAMIC_DRAW);
  }

  private visit(doc: Document, n: NodeData, parentM: Mat, alpha: number, clip: [number, number, number, number]) {
    const i0 = this.inst.length,
      m0 = this.mesh.length;
    const b0 = this.batches.length,
      lastCount = this.batches[b0 - 1]?.count ?? 0;
    try {
      this.visitInner(doc, n, parentM, alpha, clip, i0, m0);
    } catch (e) {
      // one layer that can't be drawn mustn't blank the whole canvas: it's left out
      this.inst.length = i0;
      this.mesh.length = m0;
      this.batches.length = b0;
      if (b0) this.batches[b0 - 1].count = lastCount;
      if (this.patching) this.patchFailed = true;
      if (!this.brokenLayers.has(n.id)) {
        this.brokenLayers.add(n.id);
        console.error(`[vellum] can't draw layer ${n.id} (${n.name}):`, e);
      }
    }
  }

  /** layers that failed to draw (reported once each) */
  private brokenLayers = new Set<NodeId>();

  private visitInner(doc: Document, n: NodeData, parentM: Mat, alpha: number, clip: [number, number, number, number], i0: number, m0: number) {
    const bm = blendIndex(n.blendMode);
    const bd = backdropOf(doc, n);
    if (bd) {
      // background blur / glass: batches of its own, composited against what's drawn below
      if (this.patching) this.patchFailed = true;
      const prev = this.backdropNow;
      this.backdropNow = bd;
      this.splitBatch = true;
      this.visitNode(doc, n, parentM, alpha, clip);
      this.backdropNow = prev;
      this.splitBatch = true;
    } else if (bm) {
      // a blended layer gets batches of its own, composited onto what's drawn below it
      if (this.patching) this.patchFailed = true;
      const prev = this.blendNow;
      this.blendNow = bm;
      this.splitBatch = true;
      this.visitNode(doc, n, parentM, alpha, clip);
      this.blendNow = prev;
      this.splitBatch = true;
    } else this.visitNode(doc, n, parentM, alpha, clip);
    this.ranges.set(n.id, { i0, i1: this.inst.length, m0, m1: this.mesh.length, alpha, clip });
  }

  /**
   * Re-encode changed subtrees directly into their existing buffer ranges. Fails (→ full
   * rebuild) if a subtree now needs a different number of instances/vertices or batches.
   */
  private patch(): boolean {
    const doc = this.app.doc;
    const set = new Set([...this.changedIds].filter((id) => doc.has(id)));
    this.changedIds.clear();
    const pageId = this.app.editor.pageId;
    const roots = [...set].filter((id) => !doc.ancestors(id).some((a) => set.has(a.id)));
    if (roots.length > 5000) return false;
    const totalI = this.inst.length,
      totalM = this.mesh.length;
    let lo = Infinity,
      hi = -1,
      mlo = Infinity,
      mhi = -1;
    this.patching = true;
    this.patchFailed = false;
    try {
      for (const id of roots) {
        const n = doc.get(id)!;
        if (n.type === 'PAGE' || n.type === 'DOCUMENT') {
          if (id === pageId) continue;
          continue;
        }
        if (doc.pageOf(id)?.id !== pageId) continue;
        const r = this.ranges.get(id);
        if (!r) return false;
        this.inst.length = r.i0;
        this.mesh.length = r.m0;
        this.visit(doc, n, IDENTITY, r.alpha, r.clip);
        if (this.patchFailed || this.inst.length !== r.i1 || this.mesh.length !== r.m1) return false;
        if (r.i1 > r.i0) {
          lo = Math.min(lo, r.i0);
          hi = Math.max(hi, r.i1);
        }
        if (r.m1 > r.m0) {
          mlo = Math.min(mlo, r.m0);
          mhi = Math.max(mhi, r.m1);
        }
      }
    } finally {
      this.patching = false;
      this.inst.length = totalI;
      this.mesh.length = totalM;
    }
    const gl = this.gl;
    if (hi > lo) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
      gl.bufferSubData(gl.ARRAY_BUFFER, lo * 4, this.inst.data.subarray(lo, hi));
    }
    if (mhi > mlo) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.meshBuf);
      gl.bufferSubData(gl.ARRAY_BUFFER, mlo * 4, this.mesh.data.subarray(mlo, mhi));
    }
    return true;
  }

  private batchAt(type: Batch['type'], index: number): Batch | undefined {
    let a = 0,
      b = this.batches.length - 1;
    while (a <= b) {
      const m = (a + b) >> 1;
      const bt = this.batches[m];
      if (bt.type === type && index >= bt.start && index < bt.start + bt.count) return bt;
      if (bt.start > index || (bt.start === index && bt.type !== type)) b = m - 1;
      else a = m + 1;
    }
    return this.batches.find((bt) => bt.type === type && index >= bt.start && index < bt.start + bt.count);
  }

  private visitNode(doc: Document, n: NodeData, parentM: Mat, alpha: number, clip: [number, number, number, number]) {
    // (re)collected by pushImage on every visit, including patches
    this.imageUses.delete(n.id);
    if (!n.visible || n.opacity <= 0 || this.hidden.has(n.id)) return;
    const m = n.type === 'CONNECTOR' ? IDENTITY : doc.worldTransform(n.id);
    const a = alpha * n.opacity;
    const container = n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'GROUP' || n.type === 'SECTION';

    maskDoc = doc;
    if (needsFallback(n)) {
      // the raster already includes this layer's own opacity
      this.pushRaster(n, 'subtree', alpha, clip);
      return; // the raster includes the whole subtree
    }

    switch (n.type) {
      case 'GROUP':
        break;
      case 'TEXT':
        this.pushRaster(n, 'text', a, clip);
        break;
      case 'RECTANGLE':
        if (smoothed(n)) this.pushPath(n, m, a, clip); // squircle corners: tessellate the real outline
        else this.pushBox(n, m, a, clip, !container);
        break;
      case 'FRAME':
      case 'COMPONENT':
      case 'INSTANCE':
      case 'SECTION':
      case 'STICKY':
      case 'IMAGE':
      case 'ELLIPSE':
        // arcs, pies and donuts are paths; whole ellipses draw as boxes
        if (hasArc(n)) this.pushPath(n, m, a, clip);
        else this.pushBox(n, m, a, clip, !container || n.type === 'SECTION');
        break;
      case 'SHAPE_WITH_TEXT':
        // FigJam's squares and rounded rectangles have their own fixed corners: those are paths
        if (n.shape === 'ELLIPSE') this.pushBox(n, m, a, clip, true);
        else this.pushPath(n, m, a, clip);
        break;
      default:
        this.pushPath(n, m, a, clip);
    }
    if (n.type === 'STICKY' || n.type === 'SHAPE_WITH_TEXT') {
      if (n.characters || (n.type === 'STICKY' && n.authorName)) this.pushRaster(n, 'nodetext', a, clip);
    }
    if (n.type === 'CONNECTOR' && n.characters) this.pushRaster(n, 'subtree', a, clip);
    if (container) {
      let childClip = clip;
      if (n.clipsContent && n.type !== 'GROUP' && n.type !== 'SECTION') {
        const b = doc.worldBounds(n.id);
        childClip = [Math.max(clip[0], b.x), Math.max(clip[1], b.y), Math.min(clip[2], b.x + b.width), Math.min(clip[3], b.y + b.height)];
      }
      for (const c of doc.children(n.id)) this.visit(doc, c, m, a, childClip);
      // frame strokes are drawn on top of the content
      if (n.type !== 'GROUP' && n.type !== 'SECTION' && n.strokes.some((s) => s.visible !== false) && n.strokeWeight > 0) this.pushBoxStroke(n, m, a, clip);
    }
  }

  // ------------------------------------------------------------ instances

  private beginInst(tex: WebGLTexture | null): number {
    if (this.patching) {
      this.inst.reserve(INST_FLOATS);
      const o = this.inst.length;
      this.inst.length += INST_FLOATS;
      this.inst.data.fill(0, o, o + INST_FLOATS);
      if (tex && this.batchAt('inst', o / INST_FLOATS)?.tex !== tex) this.patchFailed = true;
      return o;
    }
    const last = this.batches[this.batches.length - 1];
    if (last && !this.splitBatch && last.type === 'inst' && (last.blend ?? 0) === this.blendNow && last.backdrop === this.backdropNow && (tex === null || last.tex === null || last.tex === tex)) {
      if (tex) last.tex = tex;
      last.count++;
    } else {
      if (this.splitBatch && this.blendNow) this.layerStarts.add(this.batches.length);
      this.batches.push({ type: 'inst', start: this.inst.length / INST_FLOATS, count: 1, tex, blend: this.blendNow || undefined, backdrop: this.backdropNow });
    }
    this.splitBatch = false;
    this.inst.reserve(INST_FLOATS);
    const o = this.inst.length;
    this.inst.length += INST_FLOATS;
    this.inst.data.fill(0, o, o + INST_FLOATS);
    return o;
  }

  private writeInst(o: number, m: Mat, w: number, h: number, radii: number[], kind: number, opacity: number, clip: number[]) {
    const d = this.inst.data;
    d[o] = m[0];
    d[o + 1] = m[1];
    d[o + 2] = m[2];
    d[o + 3] = m[3];
    d[o + 4] = m[4];
    d[o + 5] = m[5];
    d[o + 6] = w;
    d[o + 7] = h;
    d[o + 8] = radii[0];
    d[o + 9] = radii[1];
    d[o + 10] = radii[2];
    d[o + 11] = radii[3];
    d[o + 12] = kind;
    d[o + 15] = opacity;
    d[o + 32] = clip[0];
    d[o + 33] = clip[1];
    d[o + 34] = clip[2];
    d[o + 35] = clip[3];
  }

  private pushBox(n: NodeData, m: Mat, alpha: number, clip: number[], withStroke: boolean) {
    const ellipse = n.type === 'ELLIPSE' || (n.type === 'SHAPE_WITH_TEXT' && n.shape === 'ELLIPSE');
    let radii: number[] = ellipse ? [0, 0, 0, 0] : cornerRadii(n);
    if (n.type === 'SHAPE_WITH_TEXT' && !ellipse) {
      const r = n.shape === 'ROUNDED_RECTANGLE' ? Math.min(n.width, n.height) * 0.2 : Math.min(4, n.width / 2, n.height / 2);
      radii = [r, r, r, r];
    }
    const kind = ellipse ? KIND_ELLIPSE : KIND_RECT;
    // Shadows first
    for (const e of n.effects) {
      if (e.visible === false || e.type !== 'DROP_SHADOW') continue;
      const o = this.beginInst(null);
      const sm: Mat = [m[0], m[1], m[2], m[3], m[4] + e.offset.x, m[5] + e.offset.y];
      this.writeInst(o, sm, n.width, n.height, radii, ellipse ? KIND_SHADOW_ELLIPSE : KIND_SHADOW_RECT, alpha, clip);
      this.setFill(o, c4(e.color));
      const d = this.inst.data;
      d[o + 41] = e.radius / 2;
      d[o + 42] = e.spread ?? 0;
    }
    const fills = n.fills.filter((p) => p.visible !== false);
    const stroke = withStroke ? n.strokes.find((p) => p.visible !== false && p.type === 'SOLID') : undefined;
    const strokeW = stroke && n.strokeWeight > 0 ? n.strokeWeight : 0;
    const dashed = !!n.strokeDash?.length;
    if (!fills.length && strokeW && !dashed) {
      const o = this.beginInst(null);
      this.writeInst(o, m, n.width, n.height, radii, kind, alpha, clip);
      this.setStroke(o, stroke as Extract<Paint, { type: 'SOLID' }>, strokeW, n.strokeAlign);
      return;
    }
    fills.forEach((p, i) => {
      const last = i === fills.length - 1;
      if (p.type === 'IMAGE') {
        this.pushImage(n, p, m, radii, alpha, clip);
        if (last && strokeW && !dashed) {
          const o = this.beginInst(null);
          this.writeInst(o, m, n.width, n.height, radii, kind, alpha, clip);
          this.setStroke(o, stroke as Extract<Paint, { type: 'SOLID' }>, strokeW, n.strokeAlign);
        }
        return;
      }
      const o = this.beginInst(null);
      this.writeInst(o, m, n.width, n.height, radii, kind, alpha, clip);
      const d = this.inst.data;
      if (p.type === 'SOLID') {
        this.setFill(o, paintColor(p));
      } else if (p.type === 'GRADIENT_LINEAR' || p.type === 'GRADIENT_RADIAL') {
        const s0 = p.stops[0],
          s1 = p.stops[p.stops.length - 1];
        const op = p.opacity ?? 1;
        this.setFill(o, c4(s0.color, op), p.type === 'GRADIENT_LINEAR' ? FILL_LINEAR : FILL_RADIAL);
        const c2 = c4(s1.color, op);
        d[o + 20] = c2[0];
        d[o + 21] = c2[1];
        d[o + 22] = c2[2];
        d[o + 23] = c2[3];
        // remap so stop positions other than 0/1 still land correctly
        const from = p.from ?? { x: 0.5, y: 0 },
          to = p.to ?? { x: 0.5, y: 1 };
        const t0 = s0.position,
          t1 = s1.position;
        const dir = { x: to.x - from.x, y: to.y - from.y };
        d[o + 24] = from.x + dir.x * t0;
        d[o + 25] = from.y + dir.y * t0;
        d[o + 26] = from.x + dir.x * t1;
        d[o + 27] = from.y + dir.y * t1;
      }
      if (last && strokeW && !dashed) this.setStroke(o, stroke as Extract<Paint, { type: 'SOLID' }>, strokeW, n.strokeAlign);
    });
    if (strokeW && dashed) this.pushPathStroke(n, m, alpha, clip);
  }

  private pushBoxStroke(n: NodeData, m: Mat, alpha: number, clip: number[]) {
    const stroke = n.strokes.find((p) => p.visible !== false && p.type === 'SOLID') as Extract<Paint, { type: 'SOLID' }> | undefined;
    if (!stroke) return;
    if (n.strokeDash?.length) return this.pushPathStroke(n, m, alpha, clip);
    const o = this.beginInst(null);
    this.writeInst(o, m, n.width, n.height, cornerRadii(n), KIND_RECT, alpha, clip);
    this.setStroke(o, stroke, n.strokeWeight, n.strokeAlign);
  }

  private setFill(o: number, c: number[], type = FILL_SOLID) {
    const d = this.inst.data;
    d[o + 16] = c[0];
    d[o + 17] = c[1];
    d[o + 18] = c[2];
    d[o + 19] = c[3];
    d[o + 40] = type;
  }

  private setStroke(o: number, p: Extract<Paint, { type: 'SOLID' }>, w: number, align: NodeData['strokeAlign']) {
    const d = this.inst.data;
    const c = paintColor(p);
    d[o + 13] = w;
    d[o + 14] = align === 'INSIDE' ? 0 : align === 'CENTER' ? 1 : 2;
    d[o + 28] = c[0];
    d[o + 29] = c[1];
    d[o + 30] = c[2];
    d[o + 31] = c[3];
  }

  private pushImage(n: NodeData, p: Extract<Paint, { type: 'IMAGE' }>, m: Mat, radii: number[], alpha: number, clip: number[]) {
    const leveled = this.app.images.leveled(p.src);
    const tex = leveled ? this.levelTexture(p.src) : this.imageTexture(p.src);
    if (!tex) {
      const o = this.beginInst(null);
      this.writeInst(o, m, n.width, n.height, radii, KIND_RECT, alpha, clip);
      this.setFill(o, [0.9, 0.9, 0.9, 1]);
      return;
    }
    // the original's size (a canvas copy is smaller but has the same proportions)
    const natural = leveled ? this.app.images.naturalSize(p.src) : undefined;
    const img = leveled ? undefined : this.app.images.peek(p.src)!;
    const iw = (natural?.width ?? img?.naturalWidth ?? n.width) || 1,
      ih = (natural?.height ?? img?.naturalHeight ?? n.height) || 1;
    let u0 = 0,
      v0 = 0,
      u1 = 1,
      v1 = 1;
    let qm = m,
      qw = n.width,
      qh = n.height;
    if (p.scaleMode === 'CROP' && p.crop) {
      // visible box [0,w]×[0,h] expressed in image uv space
      const W = iw * p.crop.scale,
        H = ih * p.crop.scale;
      u0 = -p.crop.x / W;
      v0 = -p.crop.y / H;
      u1 = (n.width - p.crop.x) / W;
      v1 = (n.height - p.crop.y) / H;
    } else if (p.scaleMode !== 'STRETCH') {
      const s = p.scaleMode === 'FIT' ? Math.min(n.width / iw, n.height / ih) : Math.max(n.width / iw, n.height / ih);
      const w = iw * s,
        h = ih * s;
      if (p.scaleMode === 'FIT') {
        qm = mul(m, [1, 0, 0, 1, (n.width - w) / 2, (n.height - h) / 2]);
        qw = w;
        qh = h;
      } else {
        const cu = (w - n.width) / 2 / w,
          cv = (h - n.height) / 2 / h;
        u0 = cu;
        u1 = 1 - cu;
        v0 = cv;
        v1 = 1 - cv;
      }
    }
    const o = this.beginInst(tex);
    this.writeInst(o, qm, qw, qh, p.scaleMode === 'FIT' ? [0, 0, 0, 0] : radii, KIND_IMAGE, alpha * (p.opacity ?? 1), clip);
    const d = this.inst.data;
    d[o + 36] = u0;
    d[o + 37] = v0;
    d[o + 38] = u1;
    d[o + 39] = v1;
    if (leveled) {
      // how sharp it has to be: the whole image's size on the page (only part of it may show)
      const sx = Math.hypot(qm[0], qm[1]),
        sy = Math.hypot(qm[2], qm[3]);
      const dim = Math.max((qw * sx) / Math.max(1e-6, u1 - u0), (qh * sy) / Math.max(1e-6, v1 - v0));
      const xs = [qm[4], qm[4] + qm[0] * qw, qm[4] + qm[2] * qh, qm[4] + qm[0] * qw + qm[2] * qh];
      const ys = [qm[5], qm[5] + qm[1] * qw, qm[5] + qm[3] * qh, qm[5] + qm[1] * qw + qm[3] * qh];
      const use = { src: p.src, x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys), dim, sized: !!natural };
      const list = this.imageUses.get(n.id);
      if (list) list.push(use);
      else this.imageUses.set(n.id, [use]);
    }
  }

  /** The texture an uploaded image draws from: gray until its first copy arrives, then ever sharper. */
  private levelTexture(src: string): WebGLTexture {
    const t = this.levelTex.get(src);
    if (t) return t.tex;
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([230, 230, 230, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.levelTex.set(src, { tex, level: 0, pixels: 1, lastSeen: performance.now() });
    return tex;
  }

  private scheduleWants() {
    if (this.wantsTimer) clearTimeout(this.wantsTimer);
    this.wantsTimer = setTimeout(() => {
      this.wantsTimer = null;
      this.wantImages();
    }, 120);
  }

  /** Ask for each uploaded image at the size it's shown: sharp on screen, small off screen. */
  private wantImages() {
    const app = this.app;
    const v = app.viewportRect();
    // a margin around the view, so panning reveals images that are already sharp
    const mx = v.width * 0.5,
      my = v.height * 0.5;
    const z = app.camera.zoom * this.dpr;
    const now = performance.now();
    const best = new Map<string, number>();
    for (const uses of this.imageUses.values())
      for (const u of uses) {
        const on = u.x1 >= v.x - mx && u.x0 <= v.x + v.width + mx && u.y1 >= v.y - my && u.y0 <= v.y + v.height + my;
        const px = on ? u.dim * z : 128;
        if (px > (best.get(u.src) ?? 0)) best.set(u.src, px);
        if (on) {
          const t = this.levelTex.get(u.src);
          if (t) t.lastSeen = now;
        }
      }
    for (const [src, px] of best) app.images.want(src, px);
    this.trimImages();
  }

  /** Copy newly arrived image copies into their textures, within a time budget. True if some are left. */
  private uploadImages(deadline: number): boolean {
    if (!this.uploads.size) return false;
    const gl = this.gl;
    for (const src of [...this.uploads]) {
      if (performance.now() > deadline) return true;
      this.uploads.delete(src);
      const got = this.app.images.take(src);
      const t = this.levelTex.get(src);
      if (!got) continue;
      if (!t) {
        got.bitmap.close();
        continue;
      }
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, got.bitmap);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      t.pixels = got.bitmap.width * got.bitmap.height;
      t.level = got.level;
      got.bitmap.close();
      // layers drawn before the image's proportions were known get their placement redone
      for (const [id, uses] of this.imageUses) if (uses.some((u) => u.src === src && !u.sized)) this.changedIds.add(id);
    }
    return false;
  }

  /** Keep image textures within a memory budget: sharp copies not seen for a while go back to small. */
  private trimImages(budget = 64e6) {
    let total = 0;
    for (const t of this.levelTex.values()) total += t.pixels;
    if (total <= budget) return;
    const now = performance.now();
    for (const [src, t] of [...this.levelTex].sort((a, b) => a[1].lastSeen - b[1].lastSeen)) {
      if (total <= budget * 0.75) break;
      if (t.level <= 256 || now - t.lastSeen < 2000) continue;
      total -= t.pixels;
      this.app.images.forget(src);
      this.app.images.want(src, 128);
    }
  }

  private imageTexture(src: string): WebGLTexture | null {
    const t = this.imageTex.get(src);
    if (t) return t;
    const img = this.app.images.get(src) as HTMLImageElement | undefined;
    if (!img) return null;
    const gl = this.gl;
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    this.imageTex.set(src, tex);
    return tex;
  }

  // ------------------------------------------------------------ meshes

  private pushTris(tris: number[], m: Mat, color: number[], clip: number[], gradient?: { p: Paint; n: NodeData }) {
    if (!tris.length) return;
    const verts = tris.length / 2;
    if (!this.patching) {
      const last = this.batches[this.batches.length - 1];
      if (last && !this.splitBatch && last.type === 'mesh' && (last.blend ?? 0) === this.blendNow && last.backdrop === this.backdropNow) last.count += verts;
      else {
        if (this.splitBatch && this.blendNow) this.layerStarts.add(this.batches.length);
        this.batches.push({ type: 'mesh', start: this.mesh.length / MESH_FLOATS, count: verts, tex: null, blend: this.blendNow || undefined, backdrop: this.backdropNow });
      }
      this.splitBatch = false;
    }
    this.mesh.reserve(verts * MESH_FLOATS);
    const d = this.mesh.data;
    let o = this.mesh.length;
    const [r, g, b, a] = color;
    for (let i = 0; i < tris.length; i += 2) {
      const x = tris[i],
        y = tris[i + 1];
      d[o] = m[0] * x + m[2] * y + m[4];
      d[o + 1] = m[1] * x + m[3] * y + m[5];
      if (gradient) {
        const c = gradientAt(gradient.p, gradient.n, x, y);
        d[o + 2] = c[0] * c[3];
        d[o + 3] = c[1] * c[3];
        d[o + 4] = c[2] * c[3];
        d[o + 5] = c[3];
      } else {
        d[o + 2] = r * a;
        d[o + 3] = g * a;
        d[o + 4] = b * a;
        d[o + 5] = a;
      }
      d[o + 6] = clip[0];
      d[o + 7] = clip[1];
      d[o + 8] = clip[2];
      d[o + 9] = clip[3];
      o += MESH_FLOATS;
    }
    this.mesh.length = o;
  }

  /**
   * The zoom range meshes are built for: steps of 4× in zoom. Within a range nothing is
   * re-tessellated, so zooming doesn't rebuild the page at every step (zooming out keeps the
   * finer meshes, see render).
   */
  private geomBand(): number {
    const z = this.app.camera.zoom * this.dpr;
    return Math.pow(4, Math.max(-3, Math.ceil(Math.log(z) / Math.log(4) - 1e-9)));
  }

  /** Curve flattening tolerance in local units: at most ~0.35 device px anywhere in the range. */
  private tolerance(m: Mat): number {
    const scale = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) * this.geomBand();
    return 0.35 / Math.max(scale, 1e-3);
  }

  private baseSig(n: NodeData): string {
    const ver = this.ver(n.id);
    const c = this.geomSigs.get(n.id);
    if (c && c.ver === ver) return c.sig;
    const sig = geomSig(n);
    this.geomSigs.set(n.id, { ver, sig });
    return sig;
  }

  private geometry(n: NodeData, m: Mat): { fill: number[]; stroke: number[] } {
    const tol = this.tolerance(m);
    const base = this.baseSig(n);
    const key = n.id;
    const cached = this.geomCache.get(key);
    // straight-line outlines don't depend on zoom; curved ones are good while at least as fine as
    // needed (and not absurdly finer)
    if (cached && cached.base === base && (cached.flat || (cached.tol <= tol * 1.01 && cached.tol * 16 >= tol))) {
      if (!cached.flat) this.curved = true;
      return cached;
    }
    // off screen, a coarser mesh is fine until the layer comes into view (see render)
    if (cached && cached.base === base && this.refineRect && !this.patching) {
      const b = this.app.doc.worldBounds(n.id);
      const r = this.refineRect;
      if (b.x > r.x + r.width || b.x + b.width < r.x || b.y > r.y + r.height || b.y + b.height < r.y) {
        this.coarse.set(n.id, b);
        this.curved = true;
        return cached;
      }
    }
    const outline = nodeOutline(n) ?? [];
    const flat = !outline.some((c) => c.c === 'C');
    if (!flat) this.curved = true;
    const polys = flattenPath(outline, tol);
    const hasFill = n.fills.some((p) => p.visible !== false) && n.type !== 'LINE';
    const fill = hasFill ? fillTriangles(polys.map((p) => p.points), n.windingRule === 'EVENODD') : [];
    const stroke: number[] = [];
    if (n.strokes.some((p) => p.visible !== false) && n.strokeWeight > 0) {
      const join = (n.strokeJoin ?? 'MITER') as Join;
      const cap = ((n.strokeCap === 'ROUND' || n.strokeCap === 'SQUARE' ? n.strokeCap : 'NONE') as Cap);
      const w = n.strokeWeight;
      // closed outlines: dashes laid out per segment, as Figma does (see dashOutline)
      const dashed = n.strokeDash?.length ? dashOutline(outline, n.strokeDash, tol) : null;
      if (dashed) for (const part of dashed) strokeTriangles(part, false, w, join, cap, stroke, 8);
      else
        for (const poly of polys) {
          const parts = n.strokeDash?.length ? dashPolyline(poly.points, poly.closed, n.strokeDash) : [poly.points];
          for (const part of parts) strokeTriangles(part, poly.closed && parts.length === 1, w, join, cap, stroke, 8);
        }
      if (n.type === 'LINE') {
        const seg = lineSegment(n);
        addCap(stroke, n.startCap, seg.a, { x: -1, y: 0 }, w);
        addCap(stroke, n.endCap, seg.b, { x: 1, y: 0 }, w);
      }
    }
    const res = { fill, stroke, base, tol, flat };
    this.geomCache.set(key, res);
    if (this.geomCache.size > 20000) this.geomCache.clear();
    return res;
  }

  private pushPath(n: NodeData, m: Mat, alpha: number, clip: number[]) {
    if (n.type === 'CONNECTOR') return this.pushConnector(n, alpha, clip);
    const g = this.geometry(n, m);
    for (const p of n.fills) {
      if (p.visible === false) continue;
      if (p.type === 'SOLID') this.pushTris(g.fill, m, paintColor(p, alpha), clip);
      else if (p.type !== 'IMAGE') this.pushTris(g.fill, m, [0, 0, 0, 1], clip, { p: { ...p, opacity: (p.opacity ?? 1) * alpha } as Paint, n });
    }
    const s = n.strokes.find((p) => p.visible !== false && p.type === 'SOLID') as Extract<Paint, { type: 'SOLID' }> | undefined;
    if (s) this.pushTris(g.stroke, m, paintColor(s, alpha), clip);
  }

  private pushPathStroke(n: NodeData, m: Mat, alpha: number, clip: number[]) {
    const g = this.geometry({ ...n, fills: [] } as NodeData, m);
    const s = n.strokes.find((p) => p.visible !== false && p.type === 'SOLID') as Extract<Paint, { type: 'SOLID' }> | undefined;
    if (s) this.pushTris(g.stroke, m, paintColor(s, alpha), clip);
    this.geomCache.delete(n.id); // fills-less variant shouldn't poison the cache
  }

  private pushConnector(n: NodeData, alpha: number, clip: number[]) {
    const doc = this.app.doc;
    const geo = connectorGeometry(doc, n);
    const s = n.strokes.find((p) => p.visible !== false && p.type === 'SOLID') as Extract<Paint, { type: 'SOLID' }> | undefined;
    if (!s) return;
    let pts = geo.points;
    if (geo.kind === 'CURVED') {
      pts = flattenPath([{ c: 'M', x: pts[0].x, y: pts[0].y }, { c: 'C', x1: pts[1].x, y1: pts[1].y, x2: pts[2].x, y2: pts[2].y, x: pts[3].x, y: pts[3].y }], this.tolerance(IDENTITY))[0]?.points ?? pts;
    }
    const w = n.strokeWeight;
    const tris: number[] = [];
    const p0 = pts[0],
      p1 = pts[pts.length - 1];
    const d0 = norm(sub(p0, pts[1])),
      d1 = norm(sub(p1, pts[pts.length - 2]));
    const inset0 = capInset(n.startCap, w),
      inset1 = capInset(n.endCap, w);
    const line = [...pts];
    line[0] = { x: p0.x - d0.x * inset0, y: p0.y - d0.y * inset0 };
    line[line.length - 1] = { x: p1.x - d1.x * inset1, y: p1.y - d1.y * inset1 };
    const parts = n.strokeDash?.length ? dashPolyline(line, false, n.strokeDash) : [line];
    for (const part of parts) strokeTriangles(part, false, w, 'ROUND', 'NONE', tris, 6);
    addCap(tris, n.startCap, p0, d0, w);
    addCap(tris, n.endCap, p1, d1, w);
    this.pushTris(tris, IDENTITY, paintColor(s, alpha), clip);
  }

  // ------------------------------------------------------------ rasters

  private pushRaster(n: NodeData, kind: RasterRequest['kind'], alpha: number, clip: number[]) {
    const key = `${kind}:${n.id}`;
    const doc = this.app.doc;
    const bounds = kind === 'subtree' ? visualBounds(doc, n) : doc.worldBounds(n.id);
    const fontSize = kind === 'subtree' ? 0 : (n.fontSize ?? 16);
    this.requests.set(key, { key, id: n.id, kind, bounds, fontSize });
    const greek = kind !== 'subtree' && n.characters ? this.pushGreek(n, kind, alpha, clip, fontSize) : [];
    let r = this.rasters.get(key);
    if (r && r.gen !== r.layer.gen) {
      // its atlas layer was recycled
      this.rasters.delete(key);
      r = undefined;
    }
    const o = this.beginInst(r?.tex ?? null);
    const slot: RasterSlot = { inst: o, greek, alpha, clip };
    this.slots.set(key, slot);
    if (r) {
      r.layer.lastUsed = this.frame;
      this.writeRasterInst(slot, n, kind, r, fontSize);
    } else {
      // invisible placeholder, patched when the raster is ready; meanwhile greeking stays visible
      this.writeInst(o, IDENTITY, 0, 0, [0, 0, 0, 0], KIND_ATLAS, 0, clip);
      for (const g of greek) this.inst.data[g + 43] = 1e-9;
    }
  }

  private writeRasterInst(slot: RasterSlot, n: NodeData, kind: RasterRequest['kind'], r: Raster, fontSize: number) {
    const m = kind === 'subtree' ? IDENTITY : this.app.doc.worldTransform(n.id);
    const qm = mul(m, [1, 0, 0, 1, r.ox, r.oy]);
    const o = slot.inst;
    this.writeInst(o, qm, r.lw, r.lh, [0, 0, 0, 0], r.tex ? KIND_IMAGE : KIND_ATLAS, slot.alpha, slot.clip);
    const d = this.inst.data;
    d[o + 13] = r.tex ? 0 : r.layer.index; // atlas layer (the stroke-width slot is unused for textures)
    d[o + 14] = 0;
    d[o + 36] = r.tex ? 0 : r.x / ATLAS;
    d[o + 37] = r.tex ? 0 : r.y / ATLAS;
    d[o + 38] = r.tex ? 1 : (r.x + r.w) / ATLAS;
    d[o + 39] = r.tex ? 1 : (r.y + r.h) / ATLAS;
    d[o + 43] = fontSize ? -fontSize : 0;
    for (const g of slot.greek) d[g + 43] = fontSize;
  }

  /** Greeked text: one bar per line, shown when the text is too small to read. */
  private pushGreek(n: NodeData, kind: RasterRequest['kind'], alpha: number, clip: number[], fontSize: number): number[] {
    const style = textStyle(n);
    const box = kind === 'nodetext' ? textBox(n) : { x: 0, y: 0, width: n.width, height: n.height };
    const pad = box.x;
    const w = Math.max(1, box.width),
      h = Math.max(style.lineHeight, box.height);
    const lines = Math.max(1, Math.min(200, Math.round(h / style.lineHeight)));
    const fill = kind === 'text' ? n.fills.find((p) => p.visible !== false && p.type === 'SOLID') : null;
    const c = fill && fill.type === 'SOLID' ? paintColor(fill) : n.textColor ? c4(n.textColor) : [0, 0, 0, 1];
    const m = mul(this.app.doc.worldTransform(n.id), [1, 0, 0, 1, pad, pad]);
    const out: number[] = [];
    for (let i = 0; i < lines; i++) {
      const lw = i === lines - 1 && lines > 1 ? w * 0.6 : w;
      const bm = mul(m, [1, 0, 0, 1, 0, i * style.lineHeight + style.lineHeight * 0.3]);
      const o = this.beginInst(null);
      this.writeInst(o, bm, lw, style.lineHeight * 0.4, [0, 0, 0, 0], KIND_RECT, alpha, clip);
      this.setFill(o, [c[0], c[1], c[2], c[3] * 0.35]);
      this.inst.data[o + 43] = fontSize;
      out.push(o);
    }
    return out;
  }

  /** Rasterize visible requests that are missing or stale, patching their instances in place. */
  private processRasters(deadline: number) {
    const view = this.app.viewportRect();
    const pad = 200 / this.app.camera.zoom;
    const vis = { x: view.x - pad, y: view.y - pad, width: view.width + pad * 2, height: view.height + pad * 2 };
    const bucket = this.bucket();
    const px = this.app.camera.zoom * this.dpr;
    const doc = this.app.doc;
    let pending = 0;
    const work: { req: RasterRequest; prio: number; scale: number }[] = [];
    for (const req of this.requests.values()) {
      if (!intersects(req.bounds, vis)) continue;
      const n = doc.get(req.id);
      if (!n) continue;
      if (req.fontSize) {
        const m = doc.worldTransform(n.id);
        if (req.fontSize * Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) * px < LOD_PX) continue; // greeked
      } else if (Math.max(req.bounds.width, req.bounds.height) * px < 2) continue;
      const r = this.rasters.get(req.key);
      const version = this.rasterSig(req.key, n, req.kind);
      const scale = this.rasterScale(n, req, bucket);
      if (scale === 0) continue;
      if (!r || r.gen !== r.layer.gen) work.push({ req, prio: 0, scale });
      else if (r.version !== version) work.push({ req, prio: 1, scale });
      else if (Math.abs(Math.log2(r.scale / scale)) > 0.25) work.push({ req, prio: 2, scale });
      else if (r.partial && !covers(r, this.visibleRegion(n, req.kind), req.kind === 'subtree' ? null : n)) work.push({ req, prio: 1, scale });
      else r.layer.lastUsed = this.frame;
    }
    work.sort((a, b) => a.prio - b.prio);
    let lo = Infinity,
      hi = -1;
    for (const { req, scale } of work) {
      if (performance.now() > deadline || this.dirty) {
        pending++;
        continue;
      }
      const n = doc.get(req.id)!;
      const r = this.rasterize(n, req, scale);
      const slot = this.slots.get(req.key);
      if (!r || !slot) continue;
      this.writeRasterInst(slot, n, req.kind, r, req.fontSize);
      for (const o of [slot.inst, ...slot.greek]) {
        lo = Math.min(lo, o);
        hi = Math.max(hi, o + INST_FLOATS);
      }
    }
    if (hi > lo && !this.dirty) {
      const gl = this.gl;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
      gl.bufferSubData(gl.ARRAY_BUFFER, lo * 4, this.inst.data.subarray(lo, hi));
    }
    this.stats.pendingRasters = pending;
  }

  /** Content signature for a raster: text rasters ignore position, subtree rasters track any change. */
  private rasterSig(key: string, n: NodeData, kind: RasterRequest['kind']): string {
    // pattern fills and custom brushes also depend on their source layers
    const ver = `${this.ver(n.id)}${kind === 'subtree' ? this.sourceVersions(n.id) : ''}`;
    const c = this.sigCache.get(key);
    if (c && c.ver === ver) return c.sig;
    const sig =
      kind === 'subtree'
        ? `v${ver}`
        : JSON.stringify([n.characters, n.fontFamily, n.fontSize, n.fontWeight, n.italic, n.lineHeight, n.letterSpacing, n.textAlign, n.verticalAlign, n.textCase, n.textDecoration, n.width, n.height, n.fills, n.textColor, n.authorName, n.type, n.shape, n.textRuns, n.listStyle, n.paragraphs, n.openTypeFeatures, n.textTruncation, n.maxLines, n.textAutoResize]);
    this.sigCache.set(key, { ver, sig });
    return sig;
  }

  private rasterScale(n: NodeData, req: RasterRequest, bucket: number): number {
    const m = req.kind === 'subtree' ? IDENTITY : this.app.doc.worldTransform(n.id);
    const s = Math.sqrt(Math.abs(m[0] * m[3] - m[1] * m[2])) * bucket;
    // Cap raster size; huge nodes get a lower resolution.
    const w = req.kind === 'subtree' ? req.bounds.width : n.width + 40;
    const h = req.kind === 'subtree' ? req.bounds.height : n.height + 40;
    const maxDim = Math.max(w, h) * s;
    if (maxDim < 1) return 0;
    return s;
  }

  /** The node-local (or page, for subtrees) region currently visible, expanded for panning. */
  private visibleRegion(n: NodeData, kind: RasterRequest['kind']): Rect {
    const view = this.app.viewportRect();
    const ex = { x: view.x - view.width * 0.1, y: view.y - view.height * 0.1, width: view.width * 1.2, height: view.height * 1.2 };
    if (kind === 'subtree') return ex;
    const inv = invertMat(this.app.doc.worldTransform(n.id));
    const pts = [
      apply(inv, { x: ex.x, y: ex.y }),
      apply(inv, { x: ex.x + ex.width, y: ex.y }),
      apply(inv, { x: ex.x, y: ex.y + ex.height }),
      apply(inv, { x: ex.x + ex.width, y: ex.y + ex.height }),
    ];
    const x0 = Math.min(...pts.map((p) => p.x)),
      y0 = Math.min(...pts.map((p) => p.y));
    return { x: x0, y: y0, width: Math.max(...pts.map((p) => p.x)) - x0, height: Math.max(...pts.map((p) => p.y)) - y0 };
  }

  private rasterize(n: NodeData, req: RasterRequest, scaleIn: number): Raster | null {
    let scale = scaleIn;
    const doc = this.app.doc;
    let ox: number, oy: number, lw: number, lh: number;
    if (req.kind === 'subtree') {
      const b = visualBounds(doc, n);
      ox = b.x;
      oy = b.y;
      lw = b.width;
      lh = b.height;
    } else {
      const fs = n.fontSize ?? 16;
      const p = Math.ceil(fs * 0.6) + 2;
      ox = -p;
      oy = -p;
      lw = n.width + p * 2;
      lh = n.height + p * 2;
    }
    let partial = false;
    if (Math.max(lw, lh) * scale > ATLAS - 4) {
      // Too big for one atlas slot at this zoom: render just the visible part at full resolution.
      const vis = this.visibleRegion(n, req.kind);
      const x0 = Math.max(ox, vis.x),
        y0 = Math.max(oy, vis.y);
      const x1 = Math.min(ox + lw, vis.x + vis.width),
        y1 = Math.min(oy + lh, vis.y + vis.height);
      if (x1 <= x0 || y1 <= y0) return null;
      ox = x0;
      oy = y0;
      lw = x1 - x0;
      lh = y1 - y0;
      partial = true;
      if (Math.max(lw, lh) * scale > this.maxTex) scale = this.maxTex / Math.max(lw, lh);
    }
    const pw = Math.max(1, Math.ceil(lw * scale)),
      ph = Math.max(1, Math.ceil(lh * scale));
    const slot = partial ? { layer: this.layers[0], x: 0, y: 0 } : this.allocate(pw, ph);
    if (!slot) return null;
    let c = this.rasterCanvas;
    if (partial) {
      // exact-size canvas for big region rasters (power-of-two growth would waste memory)
      c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(pw, ph) : Object.assign(document.createElement('canvas'), { width: pw, height: ph });
    } else if (c.width < pw || c.height < ph) {
      c.width = Math.min(ATLAS, Math.max(c.width, nextPow2(pw)));
      c.height = Math.min(ATLAS, Math.max(c.height, nextPow2(ph)));
    }
    const ctx = c.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, pw + 2, ph + 2);
    const r2d = this.r2d!;
    if (req.kind === 'subtree') {
      const base: Mat = [scale, 0, 0, scale, -ox * scale, -oy * scale];
      r2d.renderSubtree(ctx, n.id, base, { images: this.app.images, shaders: this.app.shaders, hidden: this.hidden, isolated: true });
    } else if (req.kind === 'text') {
      ctx.setTransform(scale, 0, 0, scale, -ox * scale, -oy * scale);
      r2d.drawText(ctx, n, layoutNodeText(n), n.fills);
    } else {
      const box = textBox(n);
      ctx.setTransform(scale, 0, 0, scale, (box.x - ox) * scale, (box.y - oy) * scale);
      r2d.drawText(ctx, n, layoutNodeText(n), n.textColor ? [{ type: 'SOLID', color: n.textColor }] : []);
      if (n.type === 'STICKY' && n.authorName) {
        ctx.setTransform(scale, 0, 0, scale, -ox * scale, -oy * scale);
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.font = '400 12px Inter, sans-serif';
        ctx.textBaseline = 'alphabetic';
        fillTextRun(ctx, n.authorName, box.x, n.height - 12, ctx.font);
      }
    }
    const gl = this.gl;
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    let tex: WebGLTexture | undefined;
    if (partial) {
      tex = this.bigTex.get(req.key) ?? gl.createTexture()!;
      this.bigTex.set(req.key, tex);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, ctx.getImageData(0, 0, pw, ph));
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      // the instance must move to a batch bound to this texture
      this.dirty = true;
    } else {
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlasTex);
      gl.texSubImage3D(gl.TEXTURE_2D_ARRAY, 0, slot.x, slot.y, slot.layer.index, pw, ph, 1, gl.RGBA, gl.UNSIGNED_BYTE, ctx.getImageData(0, 0, pw, ph));
    }
    const raster: Raster = { layer: slot.layer, x: slot.x, y: slot.y, w: pw, h: ph, scale, version: this.rasterSig(req.key, n, req.kind), ox, oy, lw: pw / scale, lh: ph / scale, gen: slot.layer.gen, partial, tex };
    this.rasters.set(req.key, raster);
    return raster;
  }

  private allocate(w: number, h: number): { layer: AtlasLayer; x: number; y: number } | null {
    for (const l of this.layers) {
      const r = l.alloc(w, h);
      if (r) return { layer: l, ...r };
    }
    // Recycle the least recently used layer; everything drawn from it is rebuilt next frame.
    const victim = this.layers.reduce((a, b) => (a.lastUsed <= b.lastUsed ? a : b));
    if (victim.lastUsed === this.frame) return null;
    victim.reset();
    this.dirty = true;
    return null;
  }

  // ---------------------------------------------------------------- draw

  private draw() {
    const gl = this.gl;
    const app = this.app;
    gl.viewport(0, 0, this.width, this.height);
    const bg = app.doc.get(app.editor.pageId)?.backgroundColor ?? { r: 0.96, g: 0.96, b: 0.96, a: 1 };
    gl.clearColor(bg.r, bg.g, bg.b, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const { x, y, zoom } = app.camera;
    const d = this.dpr;
    const sx = (2 * zoom * d) / this.width;
    const sy = (-2 * zoom * d) / this.height;
    const tx = (2 * x * d) / this.width - 1;
    const ty = 1 - (2 * y * d) / this.height;
    const view = new Float32Array([sx, 0, 0, 0, sy, 0, tx, ty, 1]);
    let current: 'inst' | 'mesh' | null = null;
    for (let bi = 0; bi < this.batches.length; bi++) {
      const b = this.batches[bi];
      if (b.backdrop) {
        let bj = bi;
        while (bj + 1 < this.batches.length && this.batches[bj + 1].backdrop === b.backdrop) bj++;
        this.drawBackdrop(this.batches.slice(bi, bj + 1), b.backdrop, view, zoom * d);
        bi = bj;
        current = null;
        continue;
      }
      if (b.blend) {
        // consecutive batches of the same blended layer composite together
        let bj = bi;
        while (bj + 1 < this.batches.length && this.batches[bj + 1].blend === b.blend && !this.layerStarts.has(bj + 1)) bj++;
        this.drawBlended(this.batches.slice(bi, bj + 1), b.blend, view, zoom * d);
        bi = bj;
        current = null;
        continue;
      }
      if (b.type === 'inst') {
        if (current !== 'inst') {
          gl.useProgram(this.instProg);
          gl.uniformMatrix3fv(this.uViewInst, false, view);
          gl.uniform1f(this.uPxInst, zoom * d);
          gl.uniform1f(this.uLod, LOD_PX);
          gl.uniform1i(this.uAtlas, 0);
          gl.uniform1i(this.uImage, 1);
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlasTex);
          gl.bindVertexArray(this.instVao);
          current = 'inst';
        }
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, b.tex);
        // instance attribute offsets for this batch
        gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
        for (let i = 0; i < 11; i++) gl.vertexAttribPointer(1 + i, 4, gl.FLOAT, false, INST_FLOATS * 4, b.start * INST_FLOATS * 4 + i * 16);
        gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, b.count);
      } else {
        if (current !== 'mesh') {
          gl.useProgram(this.meshProg);
          gl.uniformMatrix3fv(this.uViewMesh, false, view);
          gl.bindVertexArray(this.meshVao);
          current = 'mesh';
        }
        gl.drawArrays(gl.TRIANGLES, b.start, b.count);
      }
    }
    gl.bindVertexArray(null);
  }

  /**
   * Background blur and glass: copy what's drawn so far, blur it (two passes at half resolution),
   * draw the layer alone, then composite: inside the layer's shape the blurred / magnified backdrop,
   * the layer on top.
   */
  private drawBackdrop(batches: Batch[], fx: BackdropFx, view: Float32Array, px: number) {
    const gl = this.gl;
    const w = this.width,
      h = this.height;
    this.drawBlended([], 0, view, px, true); // makes sure the layer and backdrop targets exist
    const t = this.blendTargets!;
    // 1. what's drawn so far
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, t.backdrop);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, w, h);
    // 2. blurred (device sigma = radius / 2 in device px; half resolution halves it again)
    const sigma = (fx.radius * px) / 2 / 2;
    const hasBlur = sigma > 0.3;
    if (hasBlur) {
      const bw = Math.max(1, Math.ceil(w / 2)),
        bh = Math.max(1, Math.ceil(h / 2));
      let bt = this.blurTargets;
      if (!bt || bt.w !== bw || bt.h !== bh) {
        if (bt) {
          gl.deleteTexture(bt.a);
          gl.deleteTexture(bt.b);
          gl.deleteFramebuffer(bt.fa);
          gl.deleteFramebuffer(bt.fb);
        }
        const mk = () => {
          const x = gl.createTexture()!;
          gl.bindTexture(gl.TEXTURE_2D, x);
          gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, bw, bh);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          const f = gl.createFramebuffer()!;
          gl.bindFramebuffer(gl.FRAMEBUFFER, f);
          gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, x, 0);
          return { x, f };
        };
        const A = mk(),
          B = mk();
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        bt = this.blurTargets = { w: bw, h: bh, a: A.x, b: B.x, fa: A.f, fb: B.f };
      }
      gl.useProgram(this.blurProg);
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
      gl.viewport(0, 0, bw, bh);
      gl.uniform1i(gl.getUniformLocation(this.blurProg, 'uSrc'), 4);
      gl.uniform1f(gl.getUniformLocation(this.blurProg, 'uSigma'), sigma);
      // horizontal pass from the full-size backdrop, one half-size texel per step (linear filtering
      // averages the pairs), then vertical at half size
      gl.activeTexture(gl.TEXTURE4);
      gl.bindTexture(gl.TEXTURE_2D, t.backdrop);
      gl.bindFramebuffer(gl.FRAMEBUFFER, bt.fa);
      gl.uniform2f(gl.getUniformLocation(this.blurProg, 'uDir'), 1 / bw, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindTexture(gl.TEXTURE_2D, bt.a);
      gl.bindFramebuffer(gl.FRAMEBUFFER, bt.fb);
      gl.uniform2f(gl.getUniformLocation(this.blurProg, 'uDir'), 0, 1 / bh);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, w, h);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    }
    // 3. the layer on its own
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    for (const b of batches) this.drawBatch(b, view, px);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    // 4. composite
    const prog = this.backdropProg;
    gl.useProgram(prog);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, t.layer);
    gl.activeTexture(gl.TEXTURE5);
    gl.bindTexture(gl.TEXTURE_2D, hasBlur ? this.blurTargets!.b : t.backdrop);
    gl.uniform1i(gl.getUniformLocation(prog, 'uLayer'), 2);
    gl.uniform1i(gl.getUniformLocation(prog, 'uBackdrop'), 3);
    gl.uniform1i(gl.getUniformLocation(prog, 'uBlurred'), 5);
    gl.uniform1i(gl.getUniformLocation(prog, 'uKind'), fx.kind);
    gl.uniform1f(gl.getUniformLocation(prog, 'uHasBlur'), hasBlur ? 1 : 0);
    // page → clip → uv for the center (uv y is up in GL)
    const cx = view[0] * fx.cx + view[6],
      cy = view[4] * fx.cy + view[7];
    gl.uniform2f(gl.getUniformLocation(prog, 'uCenter'), (cx + 1) / 2, (cy + 1) / 2);
    gl.uniform1f(gl.getUniformLocation(prog, 'uScale'), fx.scale);
    gl.uniform1f(gl.getUniformLocation(prog, 'uDisp'), fx.dispersion);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** Indices of batches that start a blended layer (a layer may span several batches). */
  private layerStarts = new Set<number>();

  /** Composite blended batches: copy the backdrop, draw the layer alone, then blend it in. */
  private drawBlended(batches: Batch[], mode: number, view: Float32Array, px: number, prepareOnly = false) {
    const gl = this.gl;
    const w = this.width,
      h = this.height;
    let t = this.blendTargets;
    if (!t || t.w !== w || t.h !== h) {
      if (t) {
        gl.deleteTexture(t.layer);
        gl.deleteTexture(t.backdrop);
        gl.deleteFramebuffer(t.fbo);
      }
      const tex = (fmt: number, f: number) => {
        const x = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, x);
        gl.texStorage2D(gl.TEXTURE_2D, 1, fmt, w, h);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, f);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, f);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        return x;
      };
      const layer = tex(gl.RGBA8, gl.NEAREST);
      // the canvas has no alpha channel; linear filtering lets backdrop blurs downsample smoothly
      // (full-screen composites sample texel centers, so they're unaffected)
      const backdrop = tex(gl.RGB8, gl.LINEAR);
      const fbo = gl.createFramebuffer()!;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, layer, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      t = this.blendTargets = { w, h, layer, fbo, backdrop };
    }
    if (prepareOnly) return;
    // 1. what's drawn so far
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, t.backdrop);
    gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, w, h);
    // 2. the layer on its own
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    for (const b of batches) this.drawBatch(b, view, px);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    // 3. blend it onto the backdrop
    gl.useProgram(this.blendProg);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, t.layer);
    gl.uniform1i(gl.getUniformLocation(this.blendProg, 'uLayer'), 2);
    gl.uniform1i(gl.getUniformLocation(this.blendProg, 'uBackdrop'), 3);
    gl.uniform1i(gl.getUniformLocation(this.blendProg, 'uMode'), mode);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  }

  /** One batch with its program set up from scratch (used for blended layers). */
  private drawBatch(b: Batch, view: Float32Array, px: number) {
    const gl = this.gl;
    if (b.type === 'inst') {
      gl.useProgram(this.instProg);
      gl.uniformMatrix3fv(this.uViewInst, false, view);
      gl.uniform1f(this.uPxInst, px);
      gl.uniform1f(this.uLod, LOD_PX);
      gl.uniform1i(this.uAtlas, 0);
      gl.uniform1i(this.uImage, 1);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D_ARRAY, this.atlasTex);
      gl.bindVertexArray(this.instVao);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, b.tex);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instBuf);
      for (let i = 0; i < 11; i++) gl.vertexAttribPointer(1 + i, 4, gl.FLOAT, false, INST_FLOATS * 4, b.start * INST_FLOATS * 4 + i * 16);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, b.count);
    } else {
      gl.useProgram(this.meshProg);
      gl.uniformMatrix3fv(this.uViewMesh, false, view);
      gl.bindVertexArray(this.meshVao);
      gl.drawArrays(gl.TRIANGLES, b.start, b.count);
    }
  }

  dispose() {
    // Release GPU resources but keep the context alive (the canvas may be reused on remount).
    this.offDoc?.();
    const gl = this.gl;
    gl.deleteBuffer(this.instBuf);
    gl.deleteBuffer(this.meshBuf);
    gl.deleteBuffer(this.quadBuf);
    gl.deleteTexture(this.atlasTex);
    for (const t of this.imageTex.values()) gl.deleteTexture(t);
    for (const t of this.levelTex.values()) gl.deleteTexture(t.tex);
    if (this.app.images.onLevel) this.app.images.onLevel = null;
    gl.deleteProgram(this.instProg);
    gl.deleteProgram(this.meshProg);
  }
}

// ---------------------------------------------------------------------- helpers

/** Nodes whose effects the GPU path doesn't model are rasterized whole. */
let maskDoc: Document | null = null;
function smoothed(n: NodeData): boolean {
  return !!n.cornerSmoothing && (Array.isArray(n.cornerRadius) ? n.cornerRadius.some((r) => r > 0) : n.cornerRadius > 0);
}

function needsFallback(n: NodeData): boolean {
  if (smoothed(n) && n.type !== 'RECTANGLE') return true;
  if (n.strokeWeights && n.strokes.some((p) => p.visible !== false)) return true; // per-side strokes: the 2D renderer draws the ring // smoothed frames clip and shade along the squircle
  if (n.textPath && n.type === 'TEXT') return true; // glyphs follow a path: raster the layer
  if (maskDoc && (n.type === 'GROUP' || n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') && maskDoc.children(n.id).some((c) => c.isMask)) return true;
  // Figma Draw: brushed / dynamic / variable-width strokes are filled outlines the 2D renderer draws
  if (hasDrawStroke(n) && n.strokes.some((p) => p.visible !== false)) return true;
  for (const e of n.effects as Effect[]) {
    if (e.visible === false) continue;
    if (e.type === 'LAYER_BLUR' || e.type === 'INNER_SHADOW' || e.type === 'BACKGROUND_BLUR' || e.type === 'NOISE' || e.type === 'TEXTURE' || e.type === 'GLASS' || e.type === 'SHADER') return true;
    if (e.type === 'DROP_SHADOW' && !isBoxLike(n)) return true;
  }
  for (const p of n.fills) {
    if (p.visible === false) continue;
    if ((p.type === 'GRADIENT_LINEAR' || p.type === 'GRADIENT_RADIAL') && p.stops.length > 2) return true;
    if (p.type === 'GRADIENT_ANGULAR' || p.type === 'GRADIENT_DIAMOND' || p.type === 'PATTERN' || p.type === 'SHADER') return true; // drawn by the 2D renderer
    if (p.type === 'IMAGE' && !isBoxLike(n)) return true;
    if (p.type === 'IMAGE' && p.filters && Object.values(p.filters).some((v) => v)) return true;
  }
  if (n.strokes.some((p) => p.visible !== false && p.type !== 'SOLID')) return true;
  // blended groups flatten into one raster that is then blended; blended leaves are drawn and blended directly
  if (n.blendMode && n.blendMode !== 'NORMAL' && n.blendMode !== 'PASS_THROUGH' && (n.type === 'FRAME' || n.type === 'GROUP' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'SECTION')) return true;
  // groups with opacity fade as one layer: the 2D renderer flattens them
  if (n.opacity < 1 && maskDoc && (n.type === 'FRAME' || n.type === 'GROUP' || n.type === 'COMPONENT' || n.type === 'INSTANCE' || n.type === 'SECTION') && maskDoc.children(n.id).length > 0) return true;
  return false;
}

function geomSig(n: NodeData): string {
  return [n.type, n.width, n.height, n.path, JSON.stringify(n.arcData ?? null), JSON.stringify(n.cornerRadius), n.cornerSmoothing, n.pointCount, n.innerRadius, n.shape, n.strokeWeight, n.strokeJoin, n.strokeCap, n.strokeDash?.join(','), n.windingRule, n.startCap, n.endCap, n.fills.some((p) => p.visible !== false), n.strokes.some((p) => p.visible !== false)].join('|');
}

function isBoxLike(n: NodeData) {
  if (hasArc(n)) return false;
  return ['RECTANGLE', 'FRAME', 'COMPONENT', 'INSTANCE', 'ELLIPSE', 'STICKY', 'IMAGE', 'SECTION'].includes(n.type) || (n.type === 'SHAPE_WITH_TEXT' && n.shape === 'ELLIPSE');
}

function gradientAt(p: Paint, n: NodeData, x: number, y: number): number[] {
  if (p.type !== 'GRADIENT_LINEAR' && p.type !== 'GRADIENT_RADIAL') return [0, 0, 0, 1];
  const u = x / (n.width || 1),
    v = y / (n.height || 1);
  const from = p.from ?? { x: 0.5, y: 0 },
    to = p.to ?? { x: 0.5, y: 1 };
  let t: number;
  if (p.type === 'GRADIENT_LINEAR') {
    const dx = to.x - from.x,
      dy = to.y - from.y;
    t = ((u - from.x) * dx + (v - from.y) * dy) / (dx * dx + dy * dy || 1);
  } else t = Math.hypot((u - from.x) * n.width, (v - from.y) * n.height) / (Math.hypot((to.x - from.x) * n.width, (to.y - from.y) * n.height) || 1);
  t = Math.max(0, Math.min(1, t));
  const stops = p.stops;
  let a = stops[0],
    b = stops[stops.length - 1];
  for (let i = 0; i + 1 < stops.length; i++) {
    if (t >= stops[i].position && t <= stops[i + 1].position) {
      a = stops[i];
      b = stops[i + 1];
      break;
    }
  }
  const k = b.position === a.position ? 0 : (t - a.position) / (b.position - a.position);
  const op = p.opacity ?? 1;
  return [a.color.r + (b.color.r - a.color.r) * k, a.color.g + (b.color.g - a.color.g) * k, a.color.b + (b.color.b - a.color.b) * k, (a.color.a + (b.color.a - a.color.a) * k) * op];
}

function capInset(cap: NodeData['startCap'], w: number) {
  return cap === 'ARROW' || cap === 'TRIANGLE' ? Math.max(4, w * 3) * 0.9 : 0;
}

/** Append end-cap triangles at p facing outward direction d. */
function addCap(out: number[], cap: NodeData['startCap'], p: Vec2, d: Vec2, w: number) {
  if (!cap || cap === 'NONE' || cap === 'ROUND' || cap === 'SQUARE') return;
  const size = Math.max(5, w * 3.5);
  const nx = -d.y,
    ny = d.x;
  if (cap === 'ARROW') {
    const a = { x: p.x - d.x * size + nx * size * 0.6, y: p.y - d.y * size + ny * size * 0.6 };
    const b = { x: p.x - d.x * size - nx * size * 0.6, y: p.y - d.y * size - ny * size * 0.6 };
    strokeTriangles([a, p, b], false, w, 'ROUND', 'ROUND', out, 6);
  } else if (cap === 'TRIANGLE') {
    out.push(p.x, p.y, p.x - d.x * size + nx * size * 0.55, p.y - d.y * size + ny * size * 0.55, p.x - d.x * size - nx * size * 0.55, p.y - d.y * size - ny * size * 0.55);
  } else if (cap === 'CIRCLE') {
    const c = { x: p.x - d.x * size * 0.4, y: p.y - d.y * size * 0.4 };
    const r = size * 0.4;
    for (let i = 0; i < 16; i++) {
      const a0 = (i / 16) * Math.PI * 2,
        a1 = ((i + 1) / 16) * Math.PI * 2;
      out.push(c.x, c.y, c.x + Math.cos(a0) * r, c.y + Math.sin(a0) * r, c.x + Math.cos(a1) * r, c.y + Math.sin(a1) * r);
    }
  } else if (cap === 'DIAMOND') {
    const c = { x: p.x - d.x * size * 0.5, y: p.y - d.y * size * 0.5 };
    const l = { x: c.x + nx * size * 0.4, y: c.y + ny * size * 0.4 },
      r = { x: c.x - nx * size * 0.4, y: c.y - ny * size * 0.4 },
      back = { x: p.x - d.x * size, y: p.y - d.y * size };
    out.push(p.x, p.y, l.x, l.y, back.x, back.y, p.x, p.y, back.x, back.y, r.x, r.y);
  }
}

function sub(a: Vec2, b: Vec2): Vec2 {
  return { x: a.x - b.x, y: a.y - b.y };
}
function norm(v: Vec2): Vec2 {
  const l = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / l, y: v.y / l };
}
function intersects(a: Rect, b: Rect) {
  return a.x <= b.x + b.width && b.x <= a.x + a.width && a.y <= b.y + b.height && b.y <= a.y + a.height;
}
function invertMat(m: Mat): Mat {
  const det = m[0] * m[3] - m[1] * m[2] || 1;
  const id = 1 / det;
  return [m[3] * id, -m[1] * id, -m[2] * id, m[0] * id, (m[2] * m[5] - m[3] * m[4]) * id, (m[1] * m[4] - m[0] * m[5]) * id];
}

/** Does a partial raster's region contain the needed (visible ∩ node) region? */
function covers(r: { ox: number; oy: number; lw: number; lh: number }, vis: Rect, n: NodeData | null): boolean {
  let need = vis;
  if (n) {
    const pad = Math.ceil((n.fontSize ?? 16) * 0.6) + 2;
    const x0 = Math.max(-pad, vis.x),
      y0 = Math.max(-pad, vis.y);
    const x1 = Math.min(n.width + pad, vis.x + vis.width),
      y1 = Math.min(n.height + pad, vis.y + vis.height);
    if (x1 <= x0 || y1 <= y0) return true;
    // shrink the requirement to the central 2/3 of the visible area so small pans don't re-raster
    const sx = (x1 - x0) / 6,
      sy = (y1 - y0) / 6;
    need = { x: x0 + sx, y: y0 + sy, width: x1 - x0 - 2 * sx, height: y1 - y0 - 2 * sy };
  }
  return need.x >= r.ox - 0.5 && need.y >= r.oy - 0.5 && need.x + need.width <= r.ox + r.lw + 0.5 && need.y + need.height <= r.oy + r.lh + 0.5;
}

function nextPow2(v: number) {
  return Math.pow(2, Math.ceil(Math.log2(Math.max(1, v))));
}

function program(gl: WebGL2RenderingContext, vs: string, fs: string): WebGLProgram {
  const sh = (type: number, src: string) => {
    const s = gl.createShader(type)!;
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'shader error');
    return s;
  };
  const p = gl.createProgram()!;
  gl.attachShader(p, sh(gl.VERTEX_SHADER, vs));
  gl.attachShader(p, sh(gl.FRAGMENT_SHADER, fs));
  gl.linkProgram(p);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p) ?? 'link error');
  return p;
}

export { apply };

/** The backdrop effect a layer needs from the compositor (background blur or glass), if any. */
function backdropOf(doc: Document, n: NodeData): BackdropFx | undefined {
  const e = (n.effects as Effect[]).find((x) => x.visible !== false && (x.type === 'BACKGROUND_BLUR' || x.type === 'GLASS'));
  if (!e) return undefined;
  const b = doc.worldBounds(n.id);
  const c = { cx: b.x + b.width / 2, cy: b.y + b.height / 2 };
  if (e.type === 'GLASS') return { kind: 1, radius: e.radius ?? 0, scale: 1 + (e.refraction ?? 0.5) * 0.18, dispersion: (e.dispersion ?? 0) * 0.04, ...c };
  if (e.type === 'BACKGROUND_BLUR') return { kind: 0, radius: e.blurType === 'PROGRESSIVE' ? (e.radius + (e.startRadius ?? 0)) / 2 : e.radius, scale: 1, dispersion: 0, ...c };
  return undefined;
}
